import React, { useEffect, useState } from "react";
import { Save, Upload, Palette, GripVertical, Film, Youtube, Instagram, Video } from "lucide-react";
import { api, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";

const input = "w-full border border-brand-border focus:border-brand-ink outline-none px-3 py-2.5 text-sm rounded-sm bg-white";

const SECTION_LABELS = {
    promo_video: "Vídeo de divulgação",
    categories: "Categorias",
    novidades: "Novidades",
    bestsellers: "Mais vendidos",
    featured: "Destaques",
};

const Visual = () => {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings", {
                logo_url: s.logo_url,
                primary_color: s.primary_color,
                gold_color: s.gold_color,
                hero_title: s.hero_title,
                hero_subtitle: s.hero_subtitle,
                section_order: s.section_order,
                section_visibility: s.section_visibility,
                promo_video_enabled: s.promo_video_enabled,
                promo_video_type: s.promo_video_type,
                promo_video_url: s.promo_video_url,
                promo_video_title: s.promo_video_title,
                promo_video_subtitle: s.promo_video_subtitle,
                promo_video_thumbnail: s.promo_video_thumbnail,
                whatsapp_message_template: s.whatsapp_message_template,
            });
            setS(data);
            toast.success("Configuração salva. Recarregue a loja para ver.");
        } catch (e) {
            toast.error("Erro ao salvar");
        }
        setSaving(false);
    };

    const uploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Logo muito grande (máx 5MB).");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            setS({ ...s, logo_url: `${BACKEND_URL}${data.url}` });
            toast.success("Logo enviada — clique em Salvar para aplicar");
        } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload"); }
        setUploading(false);
        e.target.value = "";
    };

    const uploadVideo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 100 * 1024 * 1024) {
            toast.error("Vídeo muito grande. Limite: 100MB. Use um link do YouTube/Instagram para vídeos maiores.");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            setS({ ...s, promo_video_type: "upload", promo_video_url: `${BACKEND_URL}${data.url}` });
            toast.success("Vídeo enviado — clique em Salvar para aplicar");
        } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload do vídeo"); }
        setUploading(false);
        e.target.value = "";
    };

    const uploadThumb = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Imagem muito grande (máx 10MB).");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            setS({ ...s, promo_video_thumbnail: `${BACKEND_URL}${data.url}` });
            toast.success("Capa enviada");
        } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload"); }
        setUploading(false);
        e.target.value = "";
    };

    const moveSection = (idx, delta) => {
        const arr = [...s.section_order];
        const ni = idx + delta;
        if (ni < 0 || ni >= arr.length) return;
        [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
        setS({ ...s, section_order: arr });
    };

    const toggleVisibility = (key) => {
        setS({ ...s, section_visibility: { ...s.section_visibility, [key]: !s.section_visibility[key] } });
    };

    if (!s) return <div className="text-brand-inkSoft" data-testid="visual-loading">Carregando…</div>;

    return (
        <div data-testid="admin-visual-page" className="space-y-8 max-w-3xl">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Identidade</div>
                    <h1 className="font-heading font-bold text-3xl text-brand-ink">Gestão visual</h1>
                    <p className="text-sm text-brand-inkSoft mt-1">Logo, cores, textos da home e organização das seções.</p>
                </div>
                <button onClick={save} disabled={saving} data-testid="visual-save" className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-brand-dark px-5 py-3 text-sm font-semibold rounded-sm disabled:opacity-60">
                    <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar alterações"}
                </button>
            </div>

            <section className="bg-white border border-brand-border rounded-sm p-6 space-y-4">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Logotipo</h3>
                <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-brand-ink rounded-sm overflow-hidden flex items-center justify-center">
                        {s.logo_url && <img src={s.logo_url} alt="Logo" loading="lazy" decoding="async" className="w-3/4 h-3/4 object-contain" />}
                    </div>
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 border border-brand-border hover:border-brand-ink text-sm font-semibold rounded-sm cursor-pointer">
                        <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Trocar logo"}
                        <input data-testid="visual-logo-upload" type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                    </label>
                    <input className={input + " flex-1"} value={s.logo_url} onChange={(e) => setS({ ...s, logo_url: e.target.value })} />
                </div>
            </section>

            <section className="bg-white border border-brand-border rounded-sm p-6 space-y-4">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Cores principais</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Cor primária (texto)</label>
                        <div className="flex gap-2">
                            <input type="color" className="h-11 w-14 border border-brand-border rounded-sm cursor-pointer" value={s.primary_color} onChange={(e) => setS({ ...s, primary_color: e.target.value })} />
                            <input data-testid="visual-primary-color" className={input + " flex-1 font-mono"} value={s.primary_color} onChange={(e) => setS({ ...s, primary_color: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Cor de destaque (dourado)</label>
                        <div className="flex gap-2">
                            <input type="color" className="h-11 w-14 border border-brand-border rounded-sm cursor-pointer" value={s.gold_color} onChange={(e) => setS({ ...s, gold_color: e.target.value })} />
                            <input data-testid="visual-gold-color" className={input + " flex-1 font-mono"} value={s.gold_color} onChange={(e) => setS({ ...s, gold_color: e.target.value })} />
                        </div>
                    </div>
                </div>
                <p className="text-xs text-brand-inkSoft flex items-center gap-1"><Palette className="w-3 h-3" /> Dica: mantenha contraste alto entre cor primária e fundo claro.</p>
            </section>

            <section className="bg-white border border-brand-border rounded-sm p-6 space-y-4">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Textos da home</h3>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Título principal</label>
                    <input data-testid="visual-hero-title" className={input} value={s.hero_title} onChange={(e) => setS({ ...s, hero_title: e.target.value })} />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Subtítulo</label>
                    <input data-testid="visual-hero-subtitle" className={input} value={s.hero_subtitle} onChange={(e) => setS({ ...s, hero_subtitle: e.target.value })} />
                </div>
            </section>

            <section className="bg-white border border-brand-border rounded-sm p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink flex items-center gap-2"><Film className="w-4 h-4 text-brand-gold" /> Vídeo de divulgação</h3>
                        <p className="text-xs text-brand-inkSoft mt-1">Adicione um vídeo (YouTube, Instagram ou upload do seu computador/celular). A chave abaixo controla se ele aparece na home.</p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer flex-shrink-0" data-testid="promo-video-toggle">
                        <span className="text-xs font-bold uppercase tracking-wider text-brand-ink">{s.promo_video_enabled ? "Ativado" : "Desativado"}</span>
                        <span className={`relative inline-block w-12 h-6 rounded-full transition-colors ${s.promo_video_enabled ? "bg-brand-success" : "bg-brand-border"}`}>
                            <input type="checkbox" className="sr-only" checked={!!s.promo_video_enabled} onChange={(e) => setS({ ...s, promo_video_enabled: e.target.checked })} />
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${s.promo_video_enabled ? "translate-x-6" : ""}`} />
                        </span>
                    </label>
                </div>

                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Tipo de vídeo</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                            { v: "youtube", label: "YouTube", Icon: Youtube },
                            { v: "instagram", label: "Instagram", Icon: Instagram },
                            { v: "upload", label: "Upload arquivo", Icon: Upload },
                            { v: "url", label: "URL direta (.mp4)", Icon: Video },
                        ].map(({ v, label, Icon }) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setS({ ...s, promo_video_type: v, promo_video_url: "" })}
                                data-testid={`promo-video-type-${v}`}
                                className={`flex flex-col items-center gap-1.5 px-3 py-3 border rounded-sm text-xs font-semibold transition-colors ${s.promo_video_type === v ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border text-brand-inkSoft hover:border-brand-ink"}`}
                            >
                                <Icon className="w-4 h-4" />
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {s.promo_video_type === "upload" ? (
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Arquivo de vídeo</label>
                        <p className="text-[11px] text-brand-inkSoft mb-2">📱 No celular: escolha "<strong>Biblioteca de vídeos / Galeria</strong>" quando o sistema perguntar.<br/>💻 No computador: vai abrir o seletor de arquivos. Formatos: MP4, MOV, WebM. Máx 100MB.</p>
                        {s.promo_video_url && (
                            <video src={s.promo_video_url} controls className="w-full max-w-md aspect-video bg-black rounded-sm mb-2" />
                        )}
                        <label className="inline-flex items-center gap-2 px-5 py-3 bg-brand-ink text-white hover:bg-brand-dark text-sm font-semibold rounded-sm cursor-pointer">
                            <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : (s.promo_video_url ? "Trocar vídeo (galeria/arquivo)" : "Escolher vídeo (galeria/arquivo)")}
                            <input data-testid="promo-video-upload" type="file" accept="video/*" className="hidden" onChange={uploadVideo} />
                        </label>
                    </div>
                ) : (
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">
                            {s.promo_video_type === "youtube" && "Link do YouTube"}
                            {s.promo_video_type === "instagram" && "Link do Instagram (post ou reel)"}
                            {s.promo_video_type === "url" && "URL direta do vídeo"}
                        </label>
                        <input
                            data-testid="promo-video-url"
                            className={input}
                            placeholder={
                                s.promo_video_type === "youtube" ? "https://youtube.com/watch?v=..." :
                                s.promo_video_type === "instagram" ? "https://instagram.com/p/..." :
                                "https://meusite.com/video.mp4"
                            }
                            value={s.promo_video_url}
                            onChange={(e) => setS({ ...s, promo_video_url: e.target.value })}
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Título acima do vídeo</label>
                        <input data-testid="promo-video-title" className={input} value={s.promo_video_title || ""} onChange={(e) => setS({ ...s, promo_video_title: e.target.value })} placeholder="Conheça nossa coleção" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Subtítulo</label>
                        <input data-testid="promo-video-subtitle" className={input} value={s.promo_video_subtitle || ""} onChange={(e) => setS({ ...s, promo_video_subtitle: e.target.value })} placeholder="Assista ao vídeo de apresentação" />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Imagem de capa (opcional)</label>
                    <p className="text-[11px] text-brand-inkSoft mb-2">Se vazio, usaremos a thumbnail do YouTube ou um fundo escuro padrão.</p>
                    {s.promo_video_thumbnail && (
                        <div className="mb-2 relative w-48 aspect-video bg-brand-bg rounded-sm overflow-hidden border border-brand-border">
                            <img src={s.promo_video_thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </div>
                    )}
                    <div className="flex gap-2">
                        <label className="inline-flex items-center gap-2 px-4 py-2.5 border border-brand-border hover:border-brand-ink text-sm font-semibold rounded-sm cursor-pointer">
                            <Upload className="w-4 h-4" /> Upload capa
                            <input type="file" accept="image/*" className="hidden" onChange={uploadThumb} />
                        </label>
                        {s.promo_video_thumbnail && (
                            <button type="button" onClick={() => setS({ ...s, promo_video_thumbnail: "" })} className="px-4 py-2.5 text-sm font-semibold text-brand-inkSoft hover:text-brand-error">Remover capa</button>
                        )}
                    </div>
                </div>
            </section>

            <section className="bg-white border border-brand-border rounded-sm p-6 space-y-3">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Ordem e visibilidade das seções</h3>
                <p className="text-xs text-brand-inkSoft">Use as setas para reorganizar. Desmarque para ocultar a seção.</p>
                <ul className="divide-y divide-brand-border">
                    {s.section_order.map((key, idx) => (
                        <li key={key} className="flex items-center gap-3 py-3" data-testid={`section-row-${key}`}>
                            <GripVertical className="w-4 h-4 text-brand-inkSoft" />
                            <div className="flex-1 font-medium text-brand-ink">{SECTION_LABELS[key] || key}</div>
                            <label className="inline-flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={!!s.section_visibility?.[key]} onChange={() => toggleVisibility(key)} /> Visível
                            </label>
                            <div className="inline-flex gap-1">
                                <button onClick={() => moveSection(idx, -1)} className="px-2 py-1 text-xs hover:bg-brand-bg rounded-sm">↑</button>
                                <button onClick={() => moveSection(idx, 1)} className="px-2 py-1 text-xs hover:bg-brand-bg rounded-sm">↓</button>
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="pt-2 border-t border-brand-border space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!s.section_visibility?.trust} onChange={() => toggleVisibility("trust")} /> Mostrar bloco de confiança
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!s.section_visibility?.leads} onChange={() => toggleVisibility("leads")} /> Mostrar formulário "Receba novidades"
                    </label>
                </div>
            </section>

            <section className="bg-white border border-brand-border rounded-sm p-6 space-y-3" data-testid="whatsapp-template-section">
                <div className="flex items-center gap-2">
                    <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink">Mensagem padrão de WhatsApp</h3>
                </div>
                <p className="text-xs text-brand-inkSoft">Esta mensagem aparece pronta quando você clicar no WhatsApp de um lead na tela <strong>Leads</strong>. Use os atalhos abaixo para personalizar.</p>
                <div className="flex flex-wrap gap-2">
                    {["{nome}", "{primeiro_nome}", "{link}"].map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => {
                                const txt = (s.whatsapp_message_template || "") + " " + v;
                                setS({ ...s, whatsapp_message_template: txt.trim() });
                            }}
                            className="text-[11px] font-mono bg-brand-bg hover:bg-brand-ink hover:text-white px-2 py-1 rounded-sm border border-brand-border transition-colors"
                        >
                            + {v}
                        </button>
                    ))}
                </div>
                <textarea
                    data-testid="whatsapp-template-input"
                    rows={5}
                    className={input + " resize-y font-mono text-sm"}
                    value={s.whatsapp_message_template || ""}
                    onChange={(e) => setS({ ...s, whatsapp_message_template: e.target.value })}
                    placeholder="Oi {nome}! Tudo bem? Aqui é da loja Vitrine Deus Proverá. Temos novidades: {link}"
                />
                <div className="text-[11px] text-brand-inkSoft space-y-1">
                    <p><code className="bg-brand-bg px-1 rounded">{"{nome}"}</code> = nome completo do lead · <code className="bg-brand-bg px-1 rounded">{"{primeiro_nome}"}</code> = só o primeiro nome · <code className="bg-brand-bg px-1 rounded">{"{link}"}</code> = link da sua loja</p>
                    <p>💡 No WhatsApp você pode usar <code className="bg-brand-bg px-1 rounded">*negrito*</code>, <code className="bg-brand-bg px-1 rounded">_itálico_</code> e emojis 🎉</p>
                </div>
            </section>
        </div>
    );
};

export default Visual;
